import { Box, Grid, Checkbox, FormControlLabel, Radio } from "@mui/material";
import FormGroup from "@mui/material/FormGroup";
import FormLabel from "@mui/material/FormLabel";
import { useRecoilState } from "recoil";
import { memo, useCallback, useState, useEffect } from "react";
import type { ComponentProps } from "react";
import {
  CheckboxLabelWrapper,
  NewRequisitionTextField,
  RadioFormControl,
  NewRequisitionTextFieldLabel,
  handleKeyDown,
  PatientSexRadioGroup,
  PatientFieldOffsetTop,
  AbnormalUltrasoundField,
  PatientGestationLabel,
  OptionLabel,
} from "./NewRequisition.styles";
import { currentNewRequisitionEdit } from "../../recoil/atoms";
import { ExclamationIcon } from "../../assets/iconComponents/ExclamationIcon";
import {
  checkDateValidation,
  checkDateValidationError,
  isAneuploidyIneligible,
  normalizeMedicalRecordNumber,
  normalizePatientPregnancyFields,
  requiresEstimatedDueDate,
} from "./helpers";
import {
  getPatientInfoGridSx,
  patientGestationColumnSx,
  patientGestationFormGroupSx,
} from "./testPanelStyles";

type NewRequisitionTextFieldProps = ComponentProps<
  typeof NewRequisitionTextField
>;

type DebouncedNewRequisitionTextFieldProps = Partial<
  Pick<
    NewRequisitionTextFieldProps,
    | "autoComplete"
    | "error"
    | "focused"
    | "fullWidth"
    | "id"
    | "name"
    | "onKeyDown"
    | "placeholder"
    | "size"
    | "variant"
  >
> & {
  label: NewRequisitionTextFieldProps["label"];
  value: string;
  onCommit: (value: string) => void;
  normalizeValue?: (value: string) => string;
};

const DebouncedNewRequisitionTextField = memo(
  ({
    autoComplete,
    error,
    focused,
    fullWidth,
    id,
    label,
    name,
    onCommit,
    normalizeValue,
    onKeyDown,
    placeholder,
    size,
    value,
    variant,
  }: DebouncedNewRequisitionTextFieldProps) => {
    const [localValue, setLocalValue] = useState(value);
    const commitLocalValue = useCallback(() => {
      const committedValue = normalizeValue
        ? normalizeValue(localValue)
        : localValue;
      setLocalValue(committedValue);
      if (committedValue !== value) {
        onCommit(committedValue);
      }
    }, [localValue, normalizeValue, onCommit, value]);

    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    useEffect(() => {
      if (localValue === value) return;
      const timeout = setTimeout(commitLocalValue, 350);
      return () => clearTimeout(timeout);
    }, [commitLocalValue, localValue, value]);

    return (
      <NewRequisitionTextField
        autoComplete={autoComplete}
        error={error}
        focused={focused}
        fullWidth={fullWidth}
        id={id}
        label={label}
        name={name}
        onBlur={commitLocalValue}
        onChange={(event) => setLocalValue(event.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        size={size}
        value={localValue}
        variant={variant}
      />
    );
  },
);

const GESTATION_FIELDS = ["isSingleton", "isTwin", "isTripleOrMore"] as const;

const PatientInformationBasic: React.FC<{
  isEditMode?: boolean;
  isReportUpdate?: boolean;
}> = ({ isEditMode = false, isReportUpdate = false }) => {
  const [newRequisition, setNewRequisition] = useRecoilState(
    currentNewRequisitionEdit,
  );

  const { requisitionForm } = newRequisition;

  const [checkDOBError, setCheckDOBError] = useState(false);
  const [checkEDDError, setCheckEDDError] = useState(false);
  const collectionDateMin = new Date().toLocaleDateString("en-CA");

  const requiresDueDate = requiresEstimatedDueDate(requisitionForm);
  const aneuploidyIneligible = isAneuploidyIneligible(requisitionForm);
  // Edits cannot change tests, so keep the patient eligible for Aneuploidy NIPT.
  const lockForAneuploidy = isEditMode && requisitionForm.aneuploidyNIPT;

  const commitTextField = useCallback(
    (
      fieldName: "firstName" | "lastName" | "medicalRecordNumber",
      value: string,
    ) => {
      setNewRequisition((currentRequisition) => ({
        ...currentRequisition,
        requisitionForm: {
          ...currentRequisition.requisitionForm,
          [fieldName]: value,
        },
      }));
    },
    [setNewRequisition],
  );
  const commitFirstName = useCallback(
    (value: string) => commitTextField("firstName", value),
    [commitTextField],
  );
  const commitLastName = useCallback(
    (value: string) => commitTextField("lastName", value),
    [commitTextField],
  );
  const commitMedicalRecordNumber = useCallback(
    (value: string) => commitTextField("medicalRecordNumber", value),
    [commitTextField],
  );

  useEffect(() => {
    if (isEditMode) return;
    const aneuFields = aneuploidyIneligible
      ? {
          disableAneuploidy: true,
          requireFetalAntigenFile: true,
          aneuploidyNIPT: false,
          aneuploidyTwinNIPT: false,
          aneuploidyOptOutFetalSex: false,
          aneuploidyIncludeRhd: false,
          isFetalAntigen: false,
          isFetalAntigenBigC: false,
          isFetalAntigenLittleC: false,
          isFetalAntigenD: false,
          isFetalAntigenDuffy: false,
          isFetalAntigenE: false,
          isFetalAntigenKell: false,
        }
      : { disableAneuploidy: false, requireFetalAntigenFile: true };

    setNewRequisition((currentRequisition) => {
      const { requisitionForm: currentForm } = currentRequisition;
      const alreadyApplied = (
        Object.keys(aneuFields) as Array<keyof typeof aneuFields>
      ).every((key) => currentForm[key] === aneuFields[key]);
      // Skip Recoil write when derived aneuploidy fields are already synced.
      if (alreadyApplied) {
        return currentRequisition;
      }
      return {
        ...currentRequisition,
        requisitionForm: {
          ...currentForm,
          ...aneuFields,
        },
      };
    });
  }, [isEditMode, aneuploidyIneligible, setNewRequisition]);

  const onChangeCheckBox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updates: Record<string, boolean | string | number> = {
      [e.target.name]: e.target.checked,
    };
    if (e.target.name === "notPregnant" && e.target.checked) {
      updates.estimatedDueDate = "";
    }
    if (
      ["isTwin", "isTripleOrMore", "isEggDonor"].includes(e.target.name) &&
      e.target.checked
    ) {
      updates.notPregnant = false;
    }
    if (
      (GESTATION_FIELDS as readonly string[]).includes(e.target.name) &&
      e.target.checked
    ) {
      for (const field of GESTATION_FIELDS) {
        if (field !== e.target.name) updates[field] = false;
      }
    }
    setNewRequisition((currentRequisition) => {
      const nextRequisitionForm = normalizePatientPregnancyFields({
        ...currentRequisition.requisitionForm,
        ...updates,
      });
      return {
        ...currentRequisition,
        requisitionForm: nextRequisitionForm,
      };
    });
  };
  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const updates: Record<string, string> = {
      [e.target.name]: e.target.value,
    };
    if (e.target.name === "sex" && e.target.value === "Male") {
      updates.estimatedDueDate = "";
    }
    setNewRequisition((currentRequisition) => {
      const nextRequisitionForm = normalizePatientPregnancyFields({
        ...currentRequisition.requisitionForm,
        ...updates,
      });
      return {
        ...currentRequisition,
        requisitionForm: nextRequisitionForm,
      };
    });
  };

  return (
    <>
      <Grid
        container
        spacing={3}
        sx={getPatientInfoGridSx(2)}
        columns={{ xs: 4, sm: 10, md: 12 }}
      >
        <Grid item md={4} sm={4} xs={4}>
          <DebouncedNewRequisitionTextField
            label={
              <NewRequisitionTextFieldLabel>
                First Name*
                {(newRequisition.isError || newRequisition.isDraftError) &&
                  !requisitionForm.firstName && <ExclamationIcon />}
              </NewRequisitionTextFieldLabel>
            }
            autoComplete="nope"
            id="new-test-patient-mi"
            variant="standard"
            size="small"
            error={
              (newRequisition.isError || newRequisition.isDraftError) &&
              !requisitionForm.firstName
                ? "Error"
                : ""
            }
            placeholder="First Name"
            onKeyDown={handleKeyDown}
            fullWidth
            focused
            value={requisitionForm.firstName}
            name="firstName"
            onCommit={commitFirstName}
          />
        </Grid>
        <Grid item md={2} sm={2} xs={4}>
          <NewRequisitionTextField
            id="new-test-patient-mi"
            label="MI"
            variant="standard"
            size="small"
            placeholder="MI"
            fullWidth
            onKeyDown={handleKeyDown}
            focused
            disabled={isReportUpdate}
            name="middleInitial"
            value={requisitionForm.middleInitial}
            onChange={onChange}
          />
        </Grid>
        <Grid item md={6} sm={4} xs={4}>
          <DebouncedNewRequisitionTextField
            label={
              <NewRequisitionTextFieldLabel>
                Last Name*
                {(newRequisition.isError || newRequisition.isDraftError) &&
                  !requisitionForm.lastName && <ExclamationIcon />}
              </NewRequisitionTextFieldLabel>
            }
            autoComplete="nope"
            id="new-test-patient-last-name"
            error={
              (newRequisition.isError || newRequisition.isDraftError) &&
              !requisitionForm.lastName
                ? "Error"
                : ""
            }
            variant="standard"
            size="small"
            placeholder="Last Name"
            fullWidth
            onKeyDown={handleKeyDown}
            focused
            value={requisitionForm.lastName}
            name="lastName"
            onCommit={commitLastName}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={3}
        sx={getPatientInfoGridSx(2)}
        columns={{ xs: 4, sm: 12, md: 12 }}
      >
        <Grid item md={4} sm={4} xs={4}>
          <DebouncedNewRequisitionTextField
            id="new-test-patient-mrn"
            label="Medical Record Number (MRN)"
            variant="standard"
            size="small"
            placeholder="MRN"
            onKeyDown={handleKeyDown}
            fullWidth
            focused
            value={requisitionForm.medicalRecordNumber}
            name="medicalRecordNumber"
            onCommit={commitMedicalRecordNumber}
            normalizeValue={normalizeMedicalRecordNumber}
          />
        </Grid>

        <Grid item md={4} sm={4} xs={4}>
          <NewRequisitionTextField
            id="new-test-patient-maternal-weight"
            label="Maternal Weight (lbs)"
            variant="standard"
            size="small"
            placeholder="Maternal Weight (lbs)"
            fullWidth
            focused
            disabled={!requiresDueDate || isReportUpdate}
            type="text"
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            value={requisitionForm.maternalWeight || ""}
            onChange={(e) => {
              const numericValue = e.target.value.replace(/\D/g, "");
              setNewRequisition({
                ...newRequisition,
                requisitionForm: {
                  ...newRequisition.requisitionForm,
                  maternalWeight: Number(numericValue),
                },
              });
            }}
          />
        </Grid>
        <Grid item md={4} sm={4} xs={4}>
          <RadioFormControl
            className="gender-form-control"
            error={newRequisition.isError && !requisitionForm.sex}
          >
            <FormLabel>
              <NewRequisitionTextFieldLabel>
                Sex*
                {newRequisition.isError && !requisitionForm.sex && (
                  <ExclamationIcon />
                )}
              </NewRequisitionTextFieldLabel>
            </FormLabel>
            <PatientSexRadioGroup
              id="new-test-patient-sex"
              value={requisitionForm.sex}
              name="sex"
              onChange={onChange}
              row
            >
              <FormControlLabel
                id="new-test-patient-female"
                value="Female"
                control={<Radio />}
                disabled={isReportUpdate}
                label="Female"
              />
              <FormControlLabel
                id="new-test-patient-male"
                value="Male"
                control={<Radio />}
                disabled={lockForAneuploidy || isReportUpdate}
                label="Male"
              />
            </PatientSexRadioGroup>
          </RadioFormControl>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={3}
        sx={getPatientInfoGridSx(2)}
        columns={{ xs: 4, sm: 12, md: 12 }}
      >
        <Grid item md={4} sm={4} xs={4}>
          <NewRequisitionTextField
            label={
              <NewRequisitionTextFieldLabel>
                Date of Birth*
                {(newRequisition.isError ||
                  newRequisition.isDraftError ||
                  checkDOBError) &&
                  (!requisitionForm.dateOfBirth ||
                    checkDateValidation("DOB", requisitionForm)) && (
                    <ExclamationIcon />
                  )}
              </NewRequisitionTextFieldLabel>
            }
            autoComplete="nope"
            id="new-test-patient-dob"
            error={
              (newRequisition.isError ||
                newRequisition.isDraftError ||
                checkDOBError) &&
              (!requisitionForm.dateOfBirth ||
                (requisitionForm.dateOfBirth &&
                  checkDateValidation("DOB", requisitionForm)))
                ? checkDateValidationError("DOB", requisitionForm)
                : ""
            }
            $showHelpertext={
              (newRequisition.isError ||
                newRequisition.isDraftError ||
                checkDOBError) &&
              Boolean(requisitionForm.dateOfBirth) &&
              checkDateValidation("DOB", requisitionForm)
            }
            variant="standard"
            size="small"
            type="date"
            $noDateValue={!requisitionForm.dateOfBirth}
            placeholder="MM-DD-YYYY"
            fullWidth
            focused
            value={requisitionForm.dateOfBirth}
            name="dateOfBirth"
            onChange={onChange}
            onBlur={() => {
              setCheckDOBError(checkDateValidation("DOB", requisitionForm));
            }}
          />
        </Grid>

        <Grid item md={4} sm={4} xs={4}>
          <NewRequisitionTextField
            label={
              <NewRequisitionTextFieldLabel>
                Estimated Due Date{requiresDueDate && "*"}
                {(newRequisition.isError || checkEDDError) &&
                  ((requiresDueDate && !requisitionForm.estimatedDueDate) ||
                    checkDateValidation("EDD", requisitionForm)) && (
                    <ExclamationIcon />
                  )}
              </NewRequisitionTextFieldLabel>
            }
            autoComplete="nope"
            id="new-test-patient-estimated-due-date"
            error={
              (newRequisition.isError || checkEDDError) &&
              requiresDueDate &&
              (!requisitionForm.estimatedDueDate ||
                (requisitionForm.estimatedDueDate &&
                  checkDateValidation("EDD", requisitionForm)))
                ? checkDateValidationError("EDD", requisitionForm)
                : ""
            }
            $showHelpertext={
              (newRequisition.isError || checkEDDError) &&
              Boolean(requisitionForm.estimatedDueDate) &&
              checkDateValidation("EDD", requisitionForm)
            }
            variant="standard"
            size="small"
            $noDateValue={!requisitionForm.estimatedDueDate}
            placeholder="MM-DD-YYYY"
            fullWidth
            disabled={!requiresDueDate}
            type={
              !requiresDueDate && !requisitionForm.estimatedDueDate
                ? "text"
                : "date"
            }
            focused
            value={requisitionForm.estimatedDueDate}
            name="estimatedDueDate"
            onChange={onChange}
            onBlur={() =>
              setCheckEDDError(checkDateValidation("EDD", requisitionForm))
            }
          />
        </Grid>

        <Grid item md={4} sm={4} xs={4}>
          <NewRequisitionTextField
            label="Collection Date (if known)"
            autoComplete="nope"
            id="new-test-patient-collection-date"
            variant="standard"
            size="small"
            type="date"
            inputProps={{ min: collectionDateMin }}
            $noDateValue={!requisitionForm.collectionDate}
            fullWidth
            focused
            disabled={isReportUpdate}
            value={requisitionForm.collectionDate || ""}
            name="collectionDate"
            onChange={onChange}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={1}
        sx={getPatientInfoGridSx(1)}
        columns={{ xs: 4, sm: 12, md: 12 }}
      >
        <Grid item xs={4} sm={12} md={6}>
          <PatientGestationLabel>Gestation</PatientGestationLabel>
          <Box sx={patientGestationColumnSx}>
            {isReportUpdate && (
              <CheckboxLabelWrapper
                control={
                  <Checkbox
                    checked={requisitionForm.isSingleton}
                    disabled={!requiresDueDate}
                    name="isSingleton"
                    onChange={onChangeCheckBox}
                  />
                }
                label={<OptionLabel $tone="muted">Singleton</OptionLabel>}
              />
            )}
            <CheckboxLabelWrapper
              control={
                <Checkbox
                  checked={requisitionForm.isTwin}
                  disabled={
                    !requiresDueDate ||
                    // Report updates swap the gestation on click instead.
                    (!isReportUpdate && requisitionForm.isTripleOrMore) ||
                    (lockForAneuploidy && requisitionForm.isEggDonor)
                  }
                  name="isTwin"
                  onChange={onChangeCheckBox}
                />
              }
              label={<OptionLabel $tone="muted">Twin</OptionLabel>}
            />
            <CheckboxLabelWrapper
              control={
                <Checkbox
                  checked={requisitionForm.isTripleOrMore}
                  disabled={
                    !requiresDueDate ||
                    (!isReportUpdate && requisitionForm.isTwin) ||
                    lockForAneuploidy
                  }
                  name="isTripleOrMore"
                  onChange={onChangeCheckBox}
                />
              }
              label={
                <OptionLabel $tone="muted">Triplets or higher</OptionLabel>
              }
            />
            <AbnormalUltrasoundField
              id="new-test-abnormal-ultrasound"
              label="Abnormal Ultrasound Findings"
              variant="standard"
              size="small"
              placeholder="Abnormal Ultrasound Findings"
              fullWidth
              focused
              disabled={isReportUpdate}
              type="text"
              value={requisitionForm.abnormalUltrasoundFindings}
              onChange={(e) =>
                setNewRequisition({
                  ...newRequisition,
                  requisitionForm: {
                    ...newRequisition.requisitionForm,
                    abnormalUltrasoundFindings: e.target.value,
                  },
                })
              }
            />
          </Box>
        </Grid>

        <Grid item xs={4} sm={12} md={6}>
          <FormLabel>&nbsp;</FormLabel>
          <FormGroup sx={patientGestationFormGroupSx}>
            <CheckboxLabelWrapper
              control={
                <Checkbox
                  checked={requisitionForm.isEggDonor}
                  disabled={
                    !requiresDueDate ||
                    (lockForAneuploidy && requisitionForm.isTwin)
                  }
                  name="isEggDonor"
                  onChange={onChangeCheckBox}
                />
              }
              label={
                <OptionLabel $tone="muted">
                  Egg donor / Gestational carrier pregnancy
                </OptionLabel>
              }
            />
            {requisitionForm.isEggDonor && (
              <PatientFieldOffsetTop
                id="new-test-patient-age-of-egg-donor"
                label="Age of Egg Donor (Years)"
                variant="standard"
                size="small"
                placeholder="Age of Egg Donor"
                onKeyDown={handleKeyDown}
                fullWidth
                focused
                disabled={!requiresDueDate || isReportUpdate}
                type="text"
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                value={requisitionForm.ageOfEggDonor || ""}
                onChange={(e) => {
                  const numericValue = e.target.value.replace(/\D/g, "");
                  setNewRequisition({
                    ...newRequisition,
                    requisitionForm: {
                      ...newRequisition.requisitionForm,
                      ageOfEggDonor: numericValue ? Number(numericValue) : 0,
                    },
                  });
                }}
              />
            )}
            <CheckboxLabelWrapper
              control={
                <Checkbox
                  checked={requisitionForm.notPregnant}
                  disabled={
                    requisitionForm.isTwin ||
                    requisitionForm.isEggDonor ||
                    requisitionForm.isTripleOrMore ||
                    lockForAneuploidy ||
                    isReportUpdate
                  }
                  name="notPregnant"
                  onChange={onChangeCheckBox}
                />
              }
              label={<OptionLabel $tone="muted">Not pregnant</OptionLabel>}
            />
            <CheckboxLabelWrapper
              control={
                <Checkbox
                  checked={requisitionForm.vanishingTwin}
                  disabled={isReportUpdate}
                  name="vanishingTwin"
                  onChange={onChangeCheckBox}
                />
              }
              label={<OptionLabel $tone="muted">Vanishing Twin</OptionLabel>}
            />
          </FormGroup>
        </Grid>
      </Grid>
    </>
  );
};
export default PatientInformationBasic;
